import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { ApiService, showToast } from './api.service';

@Component({
  selector: 'app-admin-materials',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="p-4 md:p-5 min-h-full relative bg-[#F7FAFC] flex flex-col">
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 shrink-0">
        <div>
          <h1 class="text-xl font-semibold text-slate-900 tracking-tight">Materials</h1>
          <p class="text-slate-500 text-xs">Manage materials, hierarchy, and pricing.</p>
        </div>
        <button (click)="openAddModal()" 
                class="h-10 sm:h-auto px-3 py-2 text-white rounded-lg text-xs font-semibold shadow-sm transition-opacity hover:opacity-90 flex items-center justify-center gap-2"
                style="background:linear-gradient(135deg,#2B9AD2,#1A6FA6);">
          <i class="ti ti-plus text-[16px]"></i> Add Main Material
        </button>
      </div>

      <div class="bg-white rounded-xl shadow-sm overflow-hidden flex-1 flex flex-col border border-[#E7EEF5]">
        
        <!-- Search Bar -->
        <div class="px-4 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap bg-white">
          <div class="relative flex-1 min-w-full sm:min-w-[200px] max-w-md">
            <i class="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[16px]"></i>
            <input type="text" placeholder="Search materials..." 
                   [(ngModel)]="searchQuery" (input)="onSearchChange()"
                   class="w-full h-9 pl-9 pr-3 rounded-lg text-xs border border-slate-200 bg-slate-50 focus:outline-none focus:border-[#2B9AD2] placeholder:text-slate-400 transition-all">
          </div>
          <div class="flex flex-wrap items-center gap-2 text-[11px] font-semibold text-slate-500">
            <span class="px-2.5 py-1 rounded-lg bg-[#E8F4FB] text-[#1A6FA6] border border-sky-100">
              {{ totalItems() | number }} total items
            </span>
            <span class="px-2.5 py-1 rounded-lg bg-slate-50 border border-slate-100">
              Showing {{ rangeStart() | number }}-{{ rangeEnd() | number }} of {{ filteredTotal() | number }}
            </span>
            @if (searchQuery) {
              <span class="px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 border border-amber-100">Search results</span>
            }
          </div>
          @if (isLoading()) {
            <i class="ti ti-loader animate-spin text-[#2B9AD2] text-xl"></i>
          }
        </div>

        <!-- Table View -->
        <div class="md:hidden flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50/70">
          @if (materials().length === 0 && !isLoading()) {
            <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-12 text-center text-slate-400">
              <i class="ti ti-package text-4xl mb-2 opacity-30"></i>
              <p class="font-bold text-sm">No materials found.</p>
              <p class="text-xs mt-1">Try adjusting your search or add a new material.</p>
            </div>
          }

          @for (mat of materials(); track mat.id) {
            <article class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
              <button type="button" (click)="toggleExpand(mat.id)" class="w-full p-3 flex items-start gap-3 text-left">
                <div class="w-12 h-12 rounded-lg border border-slate-100 overflow-hidden bg-slate-50 shrink-0 shadow-sm flex items-center justify-center">
                  <img [src]="mat.photo_url || defaultPhoto" [alt]="mat.name" class="w-full h-full object-cover">
                </div>
                <div class="min-w-0 flex-1">
                  <div class="flex items-start justify-between gap-2">
                    <div class="min-w-0">
                      <p class="font-semibold text-slate-900 text-sm truncate">{{ mat.name }}</p>
                      <p class="text-[10px] text-slate-400 mt-0.5">{{ mat.serial_no }}</p>
                    </div>
                    @if (mat.sub_items?.length || mat.sub_item_count > 0) {
                      <i class="ti ti-chevron-right text-slate-400 text-base transition-transform mt-0.5" [class.rotate-90]="expandedRows()[mat.id]"></i>
                    }
                  </div>
                  <div class="flex flex-wrap items-center gap-2 mt-2">
                    <span class="px-2 py-0.5 rounded-md bg-[#E8F4FB] text-[#1A6FA6] text-[10px] font-bold">{{ mat.unit }}</span>
                    @if (mat.sub_item_count > 0 || mat.sub_items?.length) {
                      <span class="px-2 py-0.5 rounded-md bg-sky-50 text-[#2B9AD2] text-[10px] font-bold">{{ mat.sub_item_count || mat.sub_items.length }} sub-items</span>
                    } @else {
                      <span class="px-2 py-0.5 rounded-md bg-slate-50 text-slate-500 text-[10px] font-bold">Leaf item</span>
                    }
                  </div>
                  <div class="grid grid-cols-2 gap-2 mt-3">
                    <div>
                      <p class="text-[9px] font-bold uppercase tracking-wider text-slate-400">Cost</p>
                      <p class="text-xs font-semibold text-slate-700">{{ mat.cost_price !== null ? (mat.cost_price | currency:'NZD') : '—' }}</p>
                    </div>
                    <div>
                      <p class="text-[9px] font-bold uppercase tracking-wider text-slate-400">Max Sell</p>
                      <p class="text-xs font-semibold text-[#1A6FA6]">{{ mat.max_sell_price !== null ? (mat.max_sell_price | currency:'NZD') : '—' }}</p>
                    </div>
                  </div>
                </div>
              </button>

              <div class="px-3 pb-3 flex items-center gap-2">
                <button (click)="openAddModal(mat.id)" class="h-9 flex-1 rounded-lg text-emerald-600 bg-emerald-50 border border-emerald-100 text-xs font-bold flex items-center justify-center gap-1.5">
                  <i class="ti ti-plus text-[16px]"></i> Sub-item
                </button>
                <button (click)="openEditModal(mat)" class="h-9 flex-1 rounded-lg text-[#1A6FA6] bg-sky-50 border border-sky-100 text-xs font-bold flex items-center justify-center gap-1.5">
                  <i class="ti ti-edit text-[16px]"></i> Edit
                </button>
                <button (click)="deleteMaterial(mat.id)" class="h-9 w-10 rounded-lg text-red-500 bg-red-50 border border-red-100 flex items-center justify-center">
                  <i class="ti ti-trash text-[16px]"></i>
                </button>
              </div>

              @if (expandedRows()[mat.id] && loadingSubItems()[mat.id]) {
                <div class="mx-3 mb-3 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-xs font-semibold text-slate-400 flex items-center gap-2">
                  <i class="ti ti-loader animate-spin text-[#2B9AD2]"></i>
                  Loading sub-items...
                </div>
              }

              @if (expandedRows()[mat.id] && mat.sub_items?.length) {
                <div class="mx-3 mb-3 rounded-lg border border-slate-100 overflow-hidden">
                  @for (sub of mat.sub_items; track sub.id) {
                    <div class="p-3 bg-slate-50/70 border-b border-slate-100 last:border-b-0">
                      <div class="flex items-start gap-3">
                        <div class="w-9 h-9 rounded-lg border border-slate-200 bg-white overflow-hidden shrink-0">
                          <img [src]="sub.photo_url || defaultPhoto" class="w-full h-full object-cover">
                        </div>
                        <div class="min-w-0 flex-1">
                          <p class="text-xs font-bold text-slate-900 truncate">{{ sub.name }}</p>
                          <p class="text-[10px] text-slate-400 truncate">{{ sub.serial_no }} · {{ sub.unit }}</p>
                          <p class="text-[11px] text-slate-600 mt-1">
                            {{ sub.cost_price | currency:'NZD' }} cost · {{ sub.max_sell_price | currency:'NZD' }} sell
                          </p>
                        </div>
                        <div class="flex items-center gap-1">
                          <button (click)="openEditModal(sub)" class="w-8 h-8 rounded-lg text-[#1A6FA6] bg-white border border-slate-100 flex items-center justify-center">
                            <i class="ti ti-edit text-[15px]"></i>
                          </button>
                          <button (click)="deleteMaterial(sub.id)" class="w-8 h-8 rounded-lg text-red-500 bg-white border border-slate-100 flex items-center justify-center">
                            <i class="ti ti-trash text-[15px]"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  }
                </div>
              }
            </article>
          }
        </div>

        <div class="hidden md:block overflow-auto flex-1">
          <table class="w-full text-left text-sm border-separate border-spacing-0">
            <thead class="sticky top-0 z-10 bg-slate-50">
              <tr>
                <th class="px-4 py-3 w-10 border-b border-slate-100"></th>
                <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">Material</th>
                <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">Serial No</th>
                <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">Unit</th>
                <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider text-right border-b border-slate-100">Cost</th>
                <th class="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider text-right border-b border-slate-100">Max Sell</th>
                <th class="px-4 py-3 w-32 text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">Actions</th>
              </tr>
            </thead>
            <tbody>
              @if (materials().length === 0 && !isLoading()) {
                <tr>
                  <td colspan="7" class="py-20 text-center">
                    <div class="max-w-xs mx-auto text-slate-400">
                      <i class="ti ti-package text-5xl mb-3 opacity-20"></i>
                      <p class="font-bold">No materials found.</p>
                      <p class="text-xs">Try adjusting your search or add a new material.</p>
                    </div>
                  </td>
                </tr>
              }
              
              @for (mat of materials(); track mat.id) {
                <!-- Main Item Row -->
                <tr class="hover:bg-slate-50/80 transition-colors cursor-pointer group" (click)="toggleExpand(mat.id)">
                   <td class="pl-4 py-3">
                    @if (mat.sub_items?.length || mat.sub_item_count > 0) {
                      <i class="ti ti-chevron-right text-slate-400 text-base transition-transform" [class.rotate-90]="expandedRows()[mat.id]"></i>
                    }
                  </td>
                  <td class="px-4 py-3">
                    <div class="flex items-center gap-3">
                      <div class="w-9 h-9 rounded-lg border border-slate-100 overflow-hidden bg-slate-50 shrink-0 shadow-sm flex items-center justify-center">
                        <img [src]="mat.photo_url || defaultPhoto" [alt]="mat.name" class="w-full h-full object-cover">
                      </div>
                      <div>
                        <p class="font-semibold text-slate-900 text-sm group-hover:text-[#2B9AD2] transition-colors">{{ mat.name }}</p>
                        @if (mat.sub_item_count > 0 || mat.sub_items?.length) {
                          <p class="text-[10px] text-[#2B9AD2] font-bold">{{ mat.sub_item_count || mat.sub_items.length }} Sub Items</p>
                        } @else {
                          <p class="text-[10px] text-slate-400">Leaf Item</p>
                        }
                      </div>
                    </div>
                  </td>
                  <td class="px-4 py-3">
                    <span class="font-mono text-xs font-semibold px-2 py-1 rounded-md bg-[#E8F4FB] text-[#1A6FA6]">{{ mat.serial_no }}</span>
                  </td>
                  <td class="px-4 py-3">
                    <span class="text-xs font-semibold text-slate-500 uppercase">{{ mat.unit }}</span>
                  </td>
                  <td class="px-4 py-3 text-right text-slate-600 text-xs">
                    {{ mat.cost_price !== null ? (mat.cost_price | currency:'NZD') : '—' }}
                  </td>
                  <td class="px-4 py-3 text-right font-semibold text-[#1A6FA6] text-sm">
                    {{ mat.max_sell_price !== null ? (mat.max_sell_price | currency:'NZD') : '—' }}
                  </td>
                  <td class="px-4 py-3">
                    <div class="flex items-center justify-center gap-2">
                      <button (click)="$event.stopPropagation(); openAddModal(mat.id)" 
                              class="p-1.5 rounded-lg text-emerald-500 hover:bg-emerald-50 transition-colors" title="Add Sub-item">
                        <i class="ti ti-plus text-[16px]"></i>
                      </button>
                      <button (click)="$event.stopPropagation(); openEditModal(mat)" 
                              class="p-1.5 rounded-lg text-slate-400 hover:text-[#2B9AD2] hover:bg-[#E8F4FB] transition-colors opacity-0 group-hover:opacity-100">
                        <i class="ti ti-edit text-[18px]"></i>
                      </button>
                      <button (click)="$event.stopPropagation(); deleteMaterial(mat.id)" 
                              class="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100">
                        <i class="ti ti-trash text-[18px]"></i>
                      </button>
                    </div>
                  </td>
                </tr>

                <!-- Sub-items Rows -->
                @if (expandedRows()[mat.id] && loadingSubItems()[mat.id]) {
                  <tr class="bg-slate-50/40">
                    <td class="pl-4 py-2.5 text-right">
                      <i class="ti ti-loader animate-spin text-[#2B9AD2] text-xs"></i>
                    </td>
                    <td colspan="6" class="px-4 py-2.5 text-xs font-semibold text-slate-400">
                      Loading sub-items...
                    </td>
                  </tr>
                }

                @if (expandedRows()[mat.id] && mat.sub_items?.length) {
                  @for (sub of mat.sub_items; track sub.id) {
                    <tr class="bg-slate-50/40 hover:bg-slate-50 transition-colors border-t border-dashed border-slate-100 group">
                      <td class="pl-4 py-2.5 text-right">
                        <i class="ti ti-arrow-forward text-slate-400 text-xs"></i>
                      </td>
                      <td class="px-4 py-2.5">
                        <div class="flex items-center gap-2 pl-4">
                           <div class="w-8 h-8 rounded-lg border border-slate-200 bg-white overflow-hidden shrink-0 flex items-center justify-center">
                             <img [src]="sub.photo_url || defaultPhoto" class="w-full h-full object-cover">
                           </div>
                           <div>
                             <p class="text-xs text-slate-800 font-bold">{{ sub.name }}</p>
                             <p class="text-[10px] text-slate-400 truncate max-w-[200px]">{{ sub.description }}</p>
                           </div>
                        </div>
                      </td>
                      <td class="px-4 py-2.5">
                        <span class="font-mono text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">{{ sub.serial_no }}</span>
                      </td>
                      <td class="px-4 py-2.5">
                        <span class="text-[10px] font-bold text-slate-500 uppercase">{{ sub.unit }}</span>
                      </td>
                      <td class="px-4 py-2.5 text-right text-slate-500 text-xs">{{ sub.cost_price | currency:'NZD' }}</td>
                      <td class="px-4 py-2.5 text-right font-semibold text-slate-800 text-xs">{{ sub.max_sell_price | currency:'NZD' }}</td>
                      <td class="px-4 py-2.5">
                        <div class="flex items-center justify-center gap-2">
                          <button (click)="openEditModal(sub)" 
                                  class="p-1.5 rounded-lg text-slate-400 hover:text-[#2B9AD2] transition-colors opacity-0 group-hover:opacity-100">
                            <i class="ti ti-edit text-[16px]"></i>
                          </button>
                          <button (click)="deleteMaterial(sub.id)" 
                                  class="p-1.5 rounded-lg text-slate-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100">
                            <i class="ti ti-trash text-[16px]"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  }
                }
              }
            </tbody>
          </table>
        </div>

        <div class="px-4 py-3 border-t border-slate-100 bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div class="text-xs text-slate-500 font-semibold">
            Page {{ currentPage() | number }} of {{ totalPages() | number }} · {{ pageSize() | number }} rows per page
          </div>
          <div class="flex items-center gap-2">
            <button type="button" (click)="goToPage(currentPage() - 1)" [disabled]="currentPage() <= 1 || isLoading()"
                    class="h-9 px-3 rounded-lg border border-slate-200 text-xs font-bold text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50">
              Previous
            </button>
            <button type="button" (click)="goToPage(currentPage() + 1)" [disabled]="currentPage() >= totalPages() || isLoading()"
                    class="h-9 px-3 rounded-lg border border-slate-200 text-xs font-bold text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50">
              Next
            </button>
          </div>
        </div>
      </div>

      <!-- Create/Edit Modal -->
      @if (showModal()) {
        <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
            <div class="flex items-center justify-between p-4 border-b border-slate-100 shrink-0 bg-slate-50/50">
              <h2 class="text-base font-semibold text-slate-900">{{ isEditing() ? 'Edit Material' : 'Add New Material' }}</h2>
              <button (click)="showModal.set(false)" class="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-slate-100 text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-all">
                <i class="ti ti-x text-[20px]"></i>
              </button>
            </div>
            
            <form #matForm="ngForm" (ngSubmit)="saveMaterial()" class="flex flex-col flex-1 overflow-hidden">
              <div class="p-4 overflow-y-auto flex-1 space-y-4">
                
                @if (formData.parent_id) {
                  <div class="bg-blue-50 border border-blue-100 p-3 rounded-lg flex items-center gap-3">
                    <i class="ti ti-info-circle text-blue-500 text-xl"></i>
                    <p class="text-[11px] text-blue-700 font-bold leading-tight">
                      You are adding a sub-item. Sub-items must have cost and sell prices.
                    </p>
                  </div>
                }

                <div>
                  <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Material Photo</label>
                  <div class="flex items-center gap-4">
                    <div class="w-16 h-16 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden shrink-0 flex items-center justify-center shadow-inner relative group/photo">
                      <img [src]="formData.photo_url || defaultPhoto" class="w-full h-full object-cover">
                      @if (formData.photo_url) {
                        <button type="button" (click)="formData.photo_url = null" 
                                class="absolute top-1 right-1 w-6 h-6 rounded-md bg-red-500 text-white flex items-center justify-center opacity-0 group-hover/photo:opacity-100 transition-opacity">
                          <i class="ti ti-x text-[12px]"></i>
                        </button>
                      }
                    </div>
                    <div class="flex-1">
                      <input type="file" (change)="onFileSelected($event)" accept="image/*" class="hidden" #fileInput>
                      <button type="button" (click)="fileInput.click()" 
                              class="h-9 px-3 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-all flex items-center gap-2 shadow-sm">
                        <i class="ti ti-camera text-[16px]"></i>
                        {{ formData.photo_url ? 'Change Photo' : 'Upload Photo' }}
                      </button>
                      <p class="text-[9px] text-slate-400 mt-2 leading-tight">Optimal size: 400x400. Square images work best.</p>
                    </div>
                  </div>
                </div>

                <div class="grid grid-cols-2 gap-3">
                  <div class="col-span-1">
                    <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Serial No / SKU</label>
                    <input type="text" name="serial_no" [(ngModel)]="formData.serial_no" required 
                           class="w-full h-9 px-3 rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:border-[#2B9AD2] focus:bg-white font-mono text-sm transition-all" placeholder="e.g. PIPE-PVC-100">
                  </div>
                  <div class="col-span-1">
                    <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Unit of Measure</label>
                    <select name="unit" [(ngModel)]="formData.unit" required
                            class="w-full h-9 px-3 rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:border-[#2B9AD2] focus:bg-white text-sm transition-all">
                      @for (u of units; track u) {
                        <option [value]="u">{{ u }}</option>
                      }
                    </select>
                  </div>
                </div>

                <div>
                  <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Material Name</label>
                  <input type="text" name="name" [(ngModel)]="formData.name" required 
                         class="w-full h-9 px-3 rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:border-[#2B9AD2] focus:bg-white text-sm transition-all" placeholder="e.g. 100mm PVC Drain Pipe">
                </div>

                <div>
                  <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Description</label>
                  <textarea name="description" [(ngModel)]="formData.description" rows="3"
                          class="w-full p-3 rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:border-[#2B9AD2] focus:bg-white text-sm transition-all resize-none" placeholder="Technical specifications, brand, size etc."></textarea>
                </div>

                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Cost Price ($)</label>
                    <div class="relative">
                      <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">$</span>
                      <input type="number" name="cost_price" [(ngModel)]="formData.cost_price" [required]="!!formData.parent_id"
                             class="w-full h-9 pl-7 pr-3 rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:border-[#2B9AD2] focus:bg-white text-sm transition-all">
                    </div>
                  </div>
                  <div>
                    <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Max Sell Price ($)</label>
                    <div class="relative">
                      <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">$</span>
                      <input type="number" name="max_sell_price" [(ngModel)]="formData.max_sell_price" [required]="!!formData.parent_id"
                             class="w-full h-9 pl-7 pr-3 rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:border-[#2B9AD2] focus:bg-white text-sm transition-all">
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="p-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-2 shrink-0">
                <button type="button" (click)="showModal.set(false)" class="px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-all">
                  Cancel
                </button>
                <button type="submit" [disabled]="!matForm.valid" 
                        class="px-3 py-2 bg-[#1A6FA6] text-white rounded-lg text-xs font-semibold shadow-sm hover:bg-[#0C3A5A] transition-all disabled:opacity-50">
                  {{ isEditing() ? 'Update Changes' : 'Create Material' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      }
    </div>
  `
})
export class AdminMaterialsComponent implements OnInit {
  api = inject(ApiService);
  materials = signal<any[]>([]);
  isLoading = signal(true);
  searchQuery = '';
  currentPage = signal(1);
  pageSize = signal(100);
  filteredTotal = signal(0);
  totalPages = signal(1);
  totalItems = signal(0);
  activeItems = signal(0);
  topLevelItems = signal(0);
  subItems = signal(0);
  expandedRows = signal<Record<number, boolean>>({});
  loadingSubItems = signal<Record<number, boolean>>({});
  showModal = signal(false);
  isEditing = signal(false);

  defaultPhoto = 'https://nzplumbinghq.co.nz/api/assets/default_mat.jpeg';
  units = ['EA','PR','EACH','MET','LEN','PACK','PARK','ROLL','COIL','SHT','BOX','JAR','SET','BAG','KIT','PKT','DAY','BG','BT','BX','CA','CL','CN','CT','JR','KG','KT','LM','LN','MM','MT','PK','PL','PT','RD','RL','SH','ST'];

  formData: any = {
    id: null,
    serial_no: '',
    parent_id: null,
    name: '',
    description: '',
    photo_url: null,
    unit: 'EA',
    cost_price: null,
    max_sell_price: null
  };

  ngOnInit() {
    this.loadMaterials();
  }

  loadMaterials() {
    this.isLoading.set(true);
    this.api.getMaterials({ search: this.searchQuery, page: this.currentPage(), limit: this.pageSize(), include_children: false }).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.materials.set(res.data?.materials || []);
          const pagination = res.data?.pagination || {};
          const summary = res.data?.summary || {};
          this.filteredTotal.set(Number(pagination.total || 0));
          this.totalPages.set(Math.max(1, Number(pagination.total_pages || 1)));
          this.totalItems.set(Number(summary.total_items || 0));
          this.activeItems.set(Number(summary.active_items || 0));
          this.topLevelItems.set(Number(summary.top_level_items || 0));
          this.subItems.set(Number(summary.sub_items || 0));
        }
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false)
    });
  }

  onSearchChange() {
    this.currentPage.set(1);
    this.loadMaterials();
  }

  rangeStart(): number {
    if (this.filteredTotal() === 0) return 0;
    return ((this.currentPage() - 1) * this.pageSize()) + 1;
  }

  rangeEnd(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.filteredTotal());
  }

  goToPage(page: number) {
    const target = Math.max(1, Math.min(page, this.totalPages()));
    if (target === this.currentPage()) return;
    this.currentPage.set(target);
    this.loadMaterials();
  }

  toggleExpand(id: number) {
    const material = this.materials().find(mat => Number(mat.id) === Number(id));
    if (!material || !(Number(material.sub_item_count || 0) > 0 || material.sub_items?.length)) return;

    const current = this.expandedRows();
    const isOpening = !current[id];
    this.expandedRows.set({
      ...current,
      [id]: isOpening
    });

    if (isOpening && Number(material.sub_item_count || 0) > 0 && !material.sub_items?.length) {
      this.loadSubItems(id);
    }
  }

  private loadSubItems(parentId: number) {
    if (this.loadingSubItems()[parentId]) return;

    this.loadingSubItems.set({
      ...this.loadingSubItems(),
      [parentId]: true
    });

    this.api.getMaterials({ parent_id: parentId, include_children: false, limit: 100 }).subscribe({
      next: (res: any) => {
        if (res.success) {
          const subItems = res.data?.materials || [];
          this.materials.update(items => items.map(item =>
            Number(item.id) === Number(parentId)
              ? { ...item, sub_items: subItems, sub_item_count: subItems.length }
              : item
          ));
        } else {
          showToast('error', res.message || 'Sub-items could not be loaded');
        }
        this.loadingSubItems.update(state => ({ ...state, [parentId]: false }));
      },
      error: () => {
        this.loadingSubItems.update(state => ({ ...state, [parentId]: false }));
        showToast('error', 'Sub-items could not be loaded');
      }
    });
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        showToast('error', 'Image size too large (max 2MB)');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.formData.photo_url = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  resetForm() {
    this.formData = {
      id: null,
      serial_no: '',
      parent_id: null,
      name: '',
      description: '',
      photo_url: null,
      unit: 'EA',
      cost_price: null,
      max_sell_price: null
    };
  }

  openAddModal(parentId: number | null = null) {
    this.resetForm();
    this.formData.parent_id = parentId;
    this.isEditing.set(false);
    this.showModal.set(true);
  }

  openEditModal(mat: any) {
    this.formData = {
      id: mat.id,
      serial_no: mat.serial_no,
      parent_id: mat.parent_id,
      name: mat.name,
      description: mat.description,
      photo_url: mat.photo_url,
      unit: mat.unit,
      cost_price: mat.cost_price,
      max_sell_price: mat.max_sell_price
    };
    this.isEditing.set(true);
    this.showModal.set(true);
  }

  saveMaterial() {
    const obs = this.isEditing() 
      ? this.api.updateMaterial(this.formData)
      : this.api.createMaterial(this.formData);

    obs.subscribe({
      next: (res: any) => {
        if (res.success) {
          showToast('success', this.isEditing() ? 'Material updated' : 'Material created');
          this.showModal.set(false);
          this.loadMaterials();
        } else {
          showToast('error', res.message);
        }
      },
      error: (err: any) => showToast('error', 'API Connection Failure')
    });
  }

  deleteMaterial(id: number) {
    if (!confirm('Are you sure you want to delete this material? This will also delete any sub-items.')) return;

    this.api.deleteMaterial(id).subscribe({
      next: (res: any) => {
        if (res.success) {
          showToast('success', 'Material deleted');
          this.loadMaterials();
        } else {
          showToast('error', res.message);
        }
      }
    });
  }
}
